/**
 * KDIGO CKD Risk Stratification
 * 
 * Based on KDIGO 2012 Clinical Practice Guideline for the Evaluation and Management of Chronic Kidney Disease
 * Kidney International Supplements (2013) 3, 1–150
 * 
 * Stratifies risk of CKD progression and complications based on eGFR and albuminuria categories
 * 
 * @module kdigoRiskStratification
 */

/**
 * Calculate KDIGO risk category based on eGFR and albuminuria
 * 
 * @param {Object} params - Patient parameters
 * @param {number} params.eGFR - Estimated glomerular filtration rate (mL/min/1.73m²)
 * @param {number} params.albuminCreatinineRatio - Urine albumin-to-creatinine ratio (mg/g)
 * @returns {Object} Risk assessment result
 */
export const calculateKDIGORisk = ({ eGFR, albuminCreatinineRatio }) => {
  // Input validation
  if (typeof eGFR !== 'number' || eGFR <= 0) {
    return {
      error: 'Valid eGFR is required (must be a positive number)',
      category: null
    };
  }

  if (typeof albuminCreatinineRatio !== 'number' || albuminCreatinineRatio < 0) {
    return {
      error: 'Valid albumin-to-creatinine ratio is required (must be a non-negative number)',
      category: null
    };
  }

  // Determine eGFR category (G1-G5)
  const gfrCategory = getGFRCategory(eGFR);
  
  // Determine albuminuria category (A1-A3)
  const albuminuriaCategory = getAlbuminuriaCategory(albuminCreatinineRatio);
  
  // Determine risk level based on KDIGO matrix
  const riskLevel = getRiskLevel(gfrCategory, albuminuriaCategory);
  
  // Get risk details
  const riskDetails = getRiskDetails(riskLevel);

  return {
    riskLevel,
    riskCategory: riskDetails.category,
    riskColor: riskDetails.color,
    riskDescription: riskDetails.description,
    recommendations: riskDetails.recommendations,
    gfrCategory: {
      code: gfrCategory,
      description: getGFRDescription(gfrCategory),
      value: eGFR
    },
    albuminuriaCategory: {
      code: albuminuriaCategory,
      description: getAlbuminuriaDescription(albuminuriaCategory),
      value: albuminCreatinineRatio
    },
    ckdStage: getCKDStage(gfrCategory),
    source: 'KDIGO 2012 Clinical Practice Guideline',
    validation: 'Evidence-based risk stratification endorsed by international nephrology guidelines',
    interpretation: {
      low: 'Low risk: Routine CKD management, annual monitoring',
      moderate: 'Moderate risk: Optimize management, monitor every 6 months',
      high: 'High risk: Nephrology referral recommended, monitor every 3-6 months',
      veryHigh: 'Very high risk: Urgent nephrology referral, close monitoring, prepare for kidney replacement therapy'
    }
  };
};

/**
 * Get eGFR category (G1-G5)
 */
function getGFRCategory(eGFR) {
  if (eGFR >= 90) return 'G1';
  if (eGFR >= 60) return 'G2';
  if (eGFR >= 45) return 'G3a';
  if (eGFR >= 30) return 'G3b';
  if (eGFR >= 15) return 'G4';
  return 'G5';
}

/**
 * Get albuminuria category (A1-A3)
 */
function getAlbuminuriaCategory(acr) {
  if (acr < 30) return 'A1';
  if (acr < 300) return 'A2';
  return 'A3';
}

/**
 * Get risk level based on KDIGO matrix
 * 
 * KDIGO Risk Matrix:
 *           A1 (<30)  A2 (30-300)  A3 (≥300)
 * G1 (≥90)    Low      Moderate      High
 * G2 (60-89)  Low      Moderate      High
 * G3a (45-59) Low      Moderate      High
 * G3b (30-44) Moderate High          Very High
 * G4 (15-29)  High     Very High     Very High
 * G5 (<15)    Very High Very High    Very High
 */
function getRiskLevel(gfrCategory, albuminuriaCategory) {
  const riskMatrix = {
    'G1': { 'A1': 'low', 'A2': 'moderate', 'A3': 'high' },
    'G2': { 'A1': 'low', 'A2': 'moderate', 'A3': 'high' },
    'G3a': { 'A1': 'low', 'A2': 'moderate', 'A3': 'high' },
    'G3b': { 'A1': 'moderate', 'A2': 'high', 'A3': 'veryHigh' },
    'G4': { 'A1': 'high', 'A2': 'veryHigh', 'A3': 'veryHigh' },
    'G5': { 'A1': 'veryHigh', 'A2': 'veryHigh', 'A3': 'veryHigh' }
  };

  return riskMatrix[gfrCategory][albuminuriaCategory];
}

/**
 * Get risk details including color, description, and recommendations
 */
function getRiskDetails(riskLevel) {
  const details = {
    low: {
      category: 'Low Risk',
      color: '#52c41a', // Green
      description: 'Low risk of CKD progression and complications. Continue routine CKD management.',
      recommendations: [
        'Annual monitoring of kidney function and albuminuria',
        'Blood pressure control (target <140/90 mmHg)',
        'Diabetes management if applicable (HbA1c <7%)',
        'Healthy lifestyle: diet, exercise, smoking cessation',
        'Avoid nephrotoxic medications (NSAIDs, contrast agents)',
        'Continue primary care management'
      ]
    },
    moderate: {
      category: 'Moderate Risk',
      color: '#faad14', // Orange
      description: 'Moderate risk of CKD progression. Optimize management and monitor closely.',
      recommendations: [
        'Monitor kidney function every 6 months',
        'Strict blood pressure control (target <130/80 mmHg)',
        'Consider ACE inhibitor or ARB if albuminuria present',
        'Optimize diabetes control if applicable',
        'Dietary protein restriction (0.8 g/kg/day)',
        'Consider nephrology consultation for management guidance',
        'Screen for CKD complications (anemia, bone disease)'
      ]
    },
    high: {
      category: 'High Risk',
      color: '#ff7a45', // Dark Orange
      description: 'High risk of CKD progression and complications. Nephrology referral recommended.',
      recommendations: [
        'Nephrology referral recommended',
        'Monitor kidney function every 3-6 months',
        'Aggressive blood pressure control (target <130/80 mmHg)',
        'ACE inhibitor or ARB therapy (if not contraindicated)',
        'Screen and treat CKD complications (anemia, bone disease, acidosis)',
        'Dietary counseling: protein, sodium, potassium restriction',
        'Discuss kidney replacement therapy options',
        'Consider cardiovascular risk reduction strategies'
      ]
    },
    veryHigh: {
      category: 'Very High Risk',
      color: '#f5222d', // Red
      description: 'Very high risk of kidney failure. Urgent nephrology referral and preparation for kidney replacement therapy.',
      recommendations: [
        'Urgent nephrology referral required',
        'Close monitoring (monthly to quarterly)',
        'Prepare for kidney replacement therapy (dialysis or transplant)',
        'Vascular access planning for hemodialysis',
        'Transplant evaluation if appropriate',
        'Aggressive management of CKD complications',
        'Medication review and dose adjustments',
        'Comprehensive dietary counseling',
        'Psychosocial support and patient education',
        'Advance care planning discussions'
      ]
    }
  };

  return details[riskLevel];
}

/**
 * Get GFR category description
 */
function getGFRDescription(category) {
  const descriptions = {
    'G1': 'Normal or high (≥90 mL/min/1.73m²)',
    'G2': 'Mildly decreased (60-89 mL/min/1.73m²)',
    'G3a': 'Mildly to moderately decreased (45-59 mL/min/1.73m²)',
    'G3b': 'Moderately to severely decreased (30-44 mL/min/1.73m²)',
    'G4': 'Severely decreased (15-29 mL/min/1.73m²)',
    'G5': 'Kidney failure (<15 mL/min/1.73m²)'
  };
  return descriptions[category];
}

/**
 * Get albuminuria category description
 */
function getAlbuminuriaDescription(category) {
  const descriptions = {
    'A1': 'Normal to mildly increased (<30 mg/g)',
    'A2': 'Moderately increased (30-300 mg/g)',
    'A3': 'Severely increased (≥300 mg/g)'
  };
  return descriptions[category];
}

/**
 * Get CKD stage based on GFR category
 */
function getCKDStage(gfrCategory) {
  const stages = {
    'G1': 'Stage 1',
    'G2': 'Stage 2',
    'G3a': 'Stage 3a',
    'G3b': 'Stage 3b',
    'G4': 'Stage 4',
    'G5': 'Stage 5'
  };
  return stages[gfrCategory];
}

/**
 * Get risk matrix for display purposes
 * Returns the complete KDIGO risk matrix
 */
export const getKDIGORiskMatrix = () => {
  return {
    title: 'KDIGO CKD Risk Stratification Matrix',
    subtitle: 'Risk of CKD Progression by GFR and Albuminuria Category',
    rows: [
      { gfr: 'G1 (≥90)', a1: 'Low', a2: 'Moderate', a3: 'High' },
      { gfr: 'G2 (60-89)', a1: 'Low', a2: 'Moderate', a3: 'High' },
      { gfr: 'G3a (45-59)', a1: 'Low', a2: 'Moderate', a3: 'High' },
      { gfr: 'G3b (30-44)', a1: 'Moderate', a2: 'High', a3: 'Very High' },
      { gfr: 'G4 (15-29)', a1: 'High', a2: 'Very High', a3: 'Very High' },
      { gfr: 'G5 (<15)', a1: 'Very High', a2: 'Very High', a3: 'Very High' }
    ],
    columns: [
      { key: 'a1', label: 'A1 (<30 mg/g)', description: 'Normal to mildly increased albuminuria' },
      { key: 'a2', label: 'A2 (30-300 mg/g)', description: 'Moderately increased albuminuria' },
      { key: 'a3', label: 'A3 (≥300 mg/g)', description: 'Severely increased albuminuria' }
    ],
    colors: {
      'Low': '#52c41a',
      'Moderate': '#faad14',
      'High': '#ff7a45',
      'Very High': '#f5222d'
    },
    source: 'KDIGO 2012 Clinical Practice Guideline for the Evaluation and Management of Chronic Kidney Disease',
    reference: 'Kidney International Supplements (2013) 3, 1–150'
  };
};

